// Libs
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { TFunction } from 'i18next';
import { Box, Button, Grid2, Stack, Typography } from '@mui/material';
// Components, Layouts, Pages
import {
  BaseAvatar,
  BaseFilters,
  BaseInputSearch,
  BaseMoreAction,
  BaseSelect,
  BaseStatus,
  BaseTable,
  BaseToolbar,
  ConfirmModal,
  Loading,
  SortComponent,
  UnderDevelopment,
} from '~/components/common';
// Others
import { IInvoice } from '~/utils/interface/invoice';
import { useAppDispatch } from '~/utils/redux/hooks';
import { getDetailClient } from '~/thunks/client/clientThunk';
import { IPaginationResponse } from '~/utils/interface/base';
import {
  IInvoiceClientDetail,
  IParamDetailClient,
  IQueryDetailClient,
  IResponseInvoiceClient,
} from '~/utils/interface/client';
import {
  DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_PAGE,
  DEFAULT_SORT_INVOICE_CLIENT,
  EMPTY_STRING,
  EMPTY_VALUE,
  FILTER_DEFAULT_VALUES,
  optionStatusInvoice,
} from '~/utils/constants/common';
import useDebounce from '~/utils/hooks/useDebounce';
import {
  IBaseMoreAction,
  IBaseOption,
  IFilterValueChange,
  IHeaderCellType,
  IResultCallApiFn,
  ISortBy,
} from '~/utils/interface/common';
import { formatCurrencyUSD, formatDate, formatTitleCase, getFullName } from '~/utils/helper/common';
import { StorageEnum } from '~/utils/enum/base';
import { adminRouteAbsolute } from '~/utils/routes/roleRoutes/admin';
import { deleteInvoice } from '~/thunks/invoice/invoiceThunk';
import { DateFormatEnum, RoleEnum, TypeDetailClientEnum } from '~/utils/enum/common';
import { clientActions, selectIsRefreshDetailClient } from '~/thunks/client/clientSlice';
import { salesAdminRouteAbsolute } from '~/utils/routes/roleRoutes/salesAdmin';
import { salesPersonRouteAbsolute } from '~/utils/routes/roleRoutes/salesPerson';
import { operationsManagerRouteAbsolute } from '~/utils/routes/roleRoutes/operationsManager';
import { warehouseLeadRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseLead';
import { warehouseStaffRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseStaff';
import { foremanRouteAbsolute } from '~/utils/routes/roleRoutes/foreman';
import { crewRouteAbsolute } from '~/utils/routes/roleRoutes/crew';
import { loaderRouteAbsolute } from '~/utils/routes/roleRoutes/loader';
// Styles, images, icons
import styles from './InvoiceClientDetailTab.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const columns = (
  t: TFunction,
  sortBy: ISortBy[],
  handleSort: (sortType: string, name: string) => void,
  onView: (item: IInvoice) => void,
  onEdit: (id: string) => void,
  onSendInvoice: (id: string) => void,
  onDelete: (id: string) => void
): IHeaderCellType<IInvoice>[] => {
  const getSortType = (name: string) => sortBy.find((item) => item.name === name)?.typeSort || EMPTY_STRING;

  return [
    {
      id: 'invoiceNumber',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_invoice', { ns: 'admin/invoice' })}</Box>
          {/* <SortComponent name='invoice number' sortType={getSortType('invoice number')} handleSort={handleSort} /> */}
        </Stack>
      ),
      render: (record: IInvoice) => record?.invoiceNumber || EMPTY_VALUE,
    },
    {
      id: 'client',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_client', { ns: 'admin/invoice' })}</Box>
          {/* <SortComponent name='client' sortType={getSortType('client')} handleSort={handleSort} /> */}
        </Stack>
      ),
      render: (record: IInvoice) =>
        record?.client ? (
          <Stack direction={'row'} gap={4} alignItems={'center'}>
            <BaseAvatar dataAccount={record.client} size={32} fontSize={14} />
            <Stack direction={'column'}>
              <Typography className={cx('textName')}>
                {getFullName({
                  firstName: record?.client?.firstName,
                  lastName: record?.client?.lastName,
                })}
              </Typography>
              <Typography className={cx('textEmail')}>{record?.client?.clientContacts?.[0]?.email}</Typography>
            </Stack>
          </Stack>
        ) : (
          EMPTY_VALUE
        ),
    },
    {
      id: 'total',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_total', { ns: 'admin/invoice' })}</Box>
          {/* <SortComponent name='total' sortType={getSortType('total')} handleSort={handleSort} /> */}
        </Stack>
      ),
      render: (record: IInvoice) => {
        return <Typography>{formatCurrencyUSD({ value: record?.total }) || EMPTY_VALUE}</Typography>;
      },
    },
    {
      id: 'amountPaid',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_amount_paid', { ns: 'admin/invoice' })}</Box>
          {/* <SortComponent name='amount paid' sortType={getSortType('amount paid')} handleSort={handleSort} /> */}
        </Stack>
      ),
      render: (record: IInvoice) => {
        return <Typography>{formatCurrencyUSD({ value: record?.amountPaid }) || EMPTY_VALUE}</Typography>;
      },
    },
    {
      id: 'balanceDue',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_balance_due', { ns: 'admin/invoice' })}</Box>
          {/* <SortComponent name='balance due' sortType={getSortType('balance due')} handleSort={handleSort} /> */}
        </Stack>
      ),
      render: (record: IInvoice) => {
        return <Typography>{formatCurrencyUSD({ value: record?.balanceDue }) || EMPTY_VALUE}</Typography>;
      },
    },
    {
      id: 'paymentTerms',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_term', { ns: 'admin/invoice' })}</Box>
          {/* <SortComponent name='term' sortType={getSortType('term')} handleSort={handleSort} /> */}
        </Stack>
      ),
      render: (record: IInvoice) => formatTitleCase(record?.paymentTerms) || EMPTY_VALUE,
    },
    {
      id: 'status',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_status', { ns: 'admin/invoice' })}</Box>
          {/* <SortComponent name='status' sortType={getSortType('status')} handleSort={handleSort} /> */}
        </Stack>
      ),
      render: (record: IInvoice) =>
        record?.status ? <BaseStatus label={record?.status || EMPTY_STRING} /> : EMPTY_VALUE,
    },
    {
      id: 'invoiceDate',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_invoice_date', { ns: 'admin/invoice' })}</Box>
          <SortComponent name='invoice date' sortType={getSortType('invoice date')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IInvoice) => {
        return <Typography>{formatDate(record?.invoiceDate, DateFormatEnum.M_D_YYYY) || EMPTY_VALUE}</Typography>;
      },
    },
    {
      id: 'dueDate',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_due_date', { ns: 'admin/invoice' })}</Box>
          {/* <SortComponent name='due date' sortType={getSortType('due date')} handleSort={handleSort} /> */}
        </Stack>
      ),
      render: (record: IInvoice) => {
        return <Typography>{formatDate(record?.dueDate, DateFormatEnum.M_D_YYYY) || EMPTY_VALUE}</Typography>;
      },
    },
    {
      id: 'action',
      label: t('table_header_title_actions', { ns: 'admin/invoice' }),
      minWidth: 100,
      align: 'center',
      render: (record: IInvoice) => {
        const actions: IBaseMoreAction[] = [
          {
            label: t('table_action_view', { ns: 'common/base' }),
            icon: (
              <img
                src={icons.tableIconActionView}
                alt={t('common_alt_img', { ns: 'common/base' })}
                width={16}
                height={16}
              />
            ),
            onClick: () => onView(record),
          },
          {
            label: t('table_action_edit', { ns: 'common/base' }),
            icon: (
              <img
                src={icons.tableIconActionEdit}
                alt={t('common_alt_img', { ns: 'common/base' })}
                width={16}
                height={16}
              />
            ),
            onClick: () => onEdit(record.id),
          },
          {
            label: t('invoice_title_send_invoice', { ns: 'admin/invoice' }),
            icon: (
              <img
                src={icons.tableIconActionSend}
                alt={t('common_alt_img', { ns: 'common/base' })}
                width={16}
                height={16}
              />
            ),
            onClick: () => onSendInvoice(record.id),
          },
          {
            label: t('table_action_delete', { ns: 'common/base' }),
            icon: (
              <img
                src={icons.tableIconActionDelete}
                alt={t('common_alt_img', { ns: 'common/base' })}
                width={16}
                height={16}
              />
            ),
            hasOtherColor: true,
            onClick: () => onDelete(record.id),
          },
        ];

        return <BaseMoreAction actions={actions} />;
      },
    },
  ];
};

const InvoiceClientDetailTab = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/client', 'common/base', 'admin/invoice']);
  const dispatch = useAppDispatch();
  const { id: clientId } = useParams();
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
  const pageSelected = useMemo<number>(() => Number(params?.page ?? DEFAULT_CURRENT_PAGE), [params?.page]);
  const limitPage = useMemo<number>(() => Number(params?.limit ?? DEFAULT_LIMIT_PAGE), [params?.limit]);
  const currentRole = JSON.parse(localStorage.getItem(StorageEnum.ROLE)!)[0];
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [invoiceClientDetail, setInvoiceClientDetail] = useState<IInvoiceClientDetail>();
  const [searchKey, setSearchKey] = useState<string>();
  const debounceSearchKey = useDebounce(searchKey, DEFAULT_DEBOUNCE_DELAY_TIME);
  const [sortBy, setSortBy] = useState<ISortBy[]>(DEFAULT_SORT_INVOICE_CLIENT);
  const [filterParams, setFilterParams] = useState<IQueryDetailClient>(FILTER_DEFAULT_VALUES);
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  const [idInvoice, setIdInvoice] = useState<string>(EMPTY_STRING);
  const [isShowConfirmDelete, setIsShowConfirmDelete] = useState<boolean>(false);

  const [paramObject, setParamObject] = useState<IQueryDetailClient>({
    page: pageSelected,
    limit: limitPage,
  });
  const [pagination, setPagination] = useState<IPaginationResponse>();
  //#endregion Declare State

  //#region Declare Memo
  const isRefreshDetailClient = useSelector(selectIsRefreshDetailClient);
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!clientId) return;

    const params: IParamDetailClient = {
      clientId: clientId,
      type: TypeDetailClientEnum.INVOICE,
      query: paramObject,
    };

    handleGetListInvoiceClient(params);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramObject]);

  useEffect(() => {
    if (!isRefreshDetailClient) return;

    const params: IParamDetailClient = {
      clientId: clientId || EMPTY_STRING,
      type: TypeDetailClientEnum.INVOICE,
      query: paramObject,
    };

    handleGetListInvoiceClient(params);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshDetailClient]);

  useEffect(() => {
    if (debounceSearchKey !== undefined) {
      setParamObject({
        ...paramObject,
        page: DEFAULT_CURRENT_PAGE,
        limit: limitPage,
        ...(debounceSearchKey ? { textSearch: debounceSearchKey } : { textSearch: undefined }),
      });

      setSearchParams({
        page: String(DEFAULT_CURRENT_PAGE),
        limit: String(limitPage),
        ...(searchKey ? { textSearch: debounceSearchKey } : {}),
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceSearchKey]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListInvoiceClient = (params: IParamDetailClient) => {
    setIsLoading(true);

    dispatch(getDetailClient(params))
      .unwrap()
      .then((res) => {
        const { responses, pagination, dueThisWeek, overdue, paidAmount30Days, totalOutstanding } =
          res.data as IResponseInvoiceClient;

        if (!responses) return;

        setInvoiceClientDetail({
          listInvoice: responses,
          dueThisWeek: dueThisWeek,
          overdue: overdue,
          paidAmount30Days: paidAmount30Days,
          totalOutstanding: totalOutstanding,
        });
        setPagination(pagination);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleSearchChange = (value: string) => {
    setSearchKey(value);
  };

  const fetchOptionsStatusInvoice = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionStatusInvoice,
      hasMoreResp: false,
    };

    return result;
  }, []);

  const handleChangeStatusInvoice = (item: IBaseOption) => {
    setParamObject({
      ...paramObject,
      page: DEFAULT_CURRENT_PAGE,
      limit: limitPage,
      invoiceStatus: item.value,
    });
  };

  const handleApplyFilter = (values: IQueryDetailClient | undefined) => {
    if (!values) {
      setFilterParams({});
      return;
    }

    setFilterParams({
      ...values,
    });

    if (values) {
      setParamObject({
        ...paramObject,
        invoiceStatus: values?.invoiceStatus,
      });
    }
  };

  const handleClearFilter = () => {
    setFilterParams(FILTER_DEFAULT_VALUES);
    setParamObject({
      ...paramObject,
      page: DEFAULT_CURRENT_PAGE,
      limit: limitPage,
      invoiceStatus: undefined,
    });
  };

  const fetchOptionFilter = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionStatusInvoice,
      hasMoreResp: false,
    };

    return result;
  }, []);

  const handleFilterStatusChange = (
    invoiceStatus: IBaseOption,
    onChange?: (value: IFilterValueChange<IQueryDetailClient>) => void
  ) => {
    if (!invoiceStatus) return;

    const { value } = invoiceStatus;

    onChange?.({ name: 'invoiceStatus', value: value });
    setFilterParams({ ...filterParams, invoiceStatus: value });
  };

  const handleSort = (sortType: string, name: string) => {
    const updatedSortBy = sortBy.map((item) => ({
      ...item,
      typeSort: item.name === name ? sortType : undefined,
    }));
    setSortBy(updatedSortBy);
    setParamObject({ ...paramObject, quoteSortBy: name, sortType: sortType });
    setSearchParams({ ...params, sortBy: name, sortType: sortType });
  };
  const handleEdit = (id: string) => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(adminRouteAbsolute.updateInvoice(id));
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(salesAdminRouteAbsolute.updateInvoice(id));
        break;
      case RoleEnum.SALES_PERSON:
        navigate(salesPersonRouteAbsolute.updateInvoice(id));
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(operationsManagerRouteAbsolute.updateInvoice(id));
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(warehouseLeadRouteAbsolute.updateInvoice(id));
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(warehouseStaffRouteAbsolute.updateInvoice(id));
        break;
      case RoleEnum.FOREMAN:
        navigate(foremanRouteAbsolute.updateInvoice(id));
        break;
      case RoleEnum.CREW:
        navigate(crewRouteAbsolute.updateInvoice(id));
        break;
      case RoleEnum.LOADER:
        navigate(loaderRouteAbsolute.updateInvoice(id));
        break;
    }
  };

  const handleView = (item: IInvoice) => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(`${adminRouteAbsolute.detailOrder(item?.order?.id)}?orderNumber=${item?.order?.orderNumber}#invoice`);
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(
          `${salesAdminRouteAbsolute.detailOrder(item?.order?.id)}?orderNumber=${item?.order?.orderNumber}#invoice`
        );
        break;
      case RoleEnum.SALES_PERSON:
        navigate(
          `${salesPersonRouteAbsolute.detailOrder(item?.order?.id)}?orderNumber=${item?.order?.orderNumber}#invoice`
        );
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(
          `${operationsManagerRouteAbsolute.detailOrder(item?.order?.id)}?orderNumber=${
            item?.order?.orderNumber
          }#invoice`
        );
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(
          `${warehouseLeadRouteAbsolute.detailOrder(item?.order?.id)}?orderNumber=${item?.order?.orderNumber}#invoice`
        );
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(
          `${warehouseStaffRouteAbsolute.detailOrder(item?.order?.id)}?orderNumber=${item?.order?.orderNumber}#invoice`
        );
        break;
      case RoleEnum.FOREMAN:
        navigate(
          `${foremanRouteAbsolute.detailOrder(item?.order?.id)}?orderNumber=${item?.order?.orderNumber}#invoice`
        );
        break;
      case RoleEnum.CREW:
        navigate(`${crewRouteAbsolute.detailOrder(item?.order?.id)}?orderNumber=${item?.order?.orderNumber}#invoice`);
        break;
      case RoleEnum.LOADER:
        navigate(`${loaderRouteAbsolute.detailOrder(item?.order?.id)}?orderNumber=${item?.order?.orderNumber}#invoice`);
        break;
    }
  };

  const handleDelete = (id: string) => {
    if (!id) return;

    setIdInvoice(id);
    setIsShowConfirmDelete(true);
  };

  const handleCloseConfirmDelete = () => {
    setIdInvoice(EMPTY_STRING);
    setIsShowConfirmDelete(false);
  };

  const handleConfirmDelete = () => {
    setIsLoading(true);

    dispatch(deleteInvoice(idInvoice))
      .unwrap()
      .then(() => {
        setIdInvoice(EMPTY_STRING);
        handleCloseConfirmDelete();
        dispatch(clientActions.setRefreshClientDetail(true));
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleSendInvoice = (id: string) => {
    handleShowUnderDevelopment();
  };

  const handleAdd = () => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(`${adminRouteAbsolute.createInvoice}?clientId=${clientId}`);
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(`${salesAdminRouteAbsolute.createInvoice}?clientId=${clientId}`);
        break;
      case RoleEnum.SALES_PERSON:
        navigate(`${salesPersonRouteAbsolute.createInvoice}?clientId=${clientId}`);
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(`${operationsManagerRouteAbsolute.createInvoice}?clientId=${clientId}`);
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(`${warehouseLeadRouteAbsolute.createInvoice}?clientId=${clientId}`);
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(`${warehouseStaffRouteAbsolute.createInvoice}?clientId=${clientId}`);
        break;
      case RoleEnum.FOREMAN:
        navigate(`${foremanRouteAbsolute.createInvoice}?clientId=${clientId}`);
        break;
      case RoleEnum.CREW:
        navigate(`${crewRouteAbsolute.createInvoice}?clientId=${clientId}`);
        break;
      case RoleEnum.LOADER:
        navigate(`${loaderRouteAbsolute.createInvoice}?clientId=${clientId}`);
        break;
    }
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
    setSearchParams({ ...params, page: String(page), limit: String(limitPage) });
  };

  const handleShowUnderDevelopment = () => {
    setIsShowUnderDevelopment(!isShowUnderDevelopment);
  };
  //#endregion Handle Function

  return (
    <Stack id='invoiceClientDetailTabComponent' className={cx('container')}>
      <Stack className={cx('viewHeader')}>
        <BaseToolbar title={t('invoice_title')} description={t('invoice_description')}>
          <Box className={cx('button')}>
            <Button
              type='button'
              variant='contained'
              className={cx('buttonAction')}
              onClick={handleAdd}
              startIcon={<img src={icons.commonIconPlus} alt={'common_alt_img'} />}
            >
              {t('invoice_add_invoice_button')}
            </Button>
          </Box>
        </BaseToolbar>
      </Stack>

      <Stack direction={'row'} spacing={4} p={6}>
        <BaseInputSearch
          width={300}
          typeStyle='outlined'
          onChange={handleSearchChange}
          borderRadius={100}
          placeholder={t('invoice_placeholder_search')}
        />

        <BaseFilters<IQueryDetailClient>
          value={filterParams}
          defaultValue={FILTER_DEFAULT_VALUES}
          onApply={handleApplyFilter}
          onClear={handleClearFilter}
        >
          {({ value, onChange }) => (
            <Stack direction={'column'} width={340} mb={4} gap={6}>
              <BaseSelect
                gap={10}
                height={40}
                styleType='contained'
                label={t('invoice_title_filter_status')}
                placeholder={t('invoice_title_filter_all_type')}
                callApiFn={fetchOptionFilter}
                value={value?.invoiceStatus}
                onChange={(option) => handleFilterStatusChange(option as any, onChange)}
              />
            </Stack>
          )}
        </BaseFilters>
      </Stack>

      <Grid2 container spacing={11} p={6}>
        <Grid2 className={cx('statistic')} size={3}>
          <Typography className={cx('titleStatistic')}>{t('invoice_title_total_outstanding')}</Typography>

          <Typography className={cx('valueStatistic', 'outstanding')}>
            {formatCurrencyUSD({ value: invoiceClientDetail?.totalOutstanding, showDecimal: true })}
          </Typography>
        </Grid2>

        <Grid2 className={cx('statistic')} size={3}>
          <Typography className={cx('titleStatistic')}>{t('invoice_title_overdue')}</Typography>

          <Typography className={cx('valueStatistic', 'overdue')}>
            {formatCurrencyUSD({ value: invoiceClientDetail?.overdue, showDecimal: true })}
          </Typography>
        </Grid2>

        <Grid2 className={cx('statistic')} size={3}>
          <Typography className={cx('titleStatistic')}>{t('invoice_title_due_this_week')}</Typography>

          <Typography className={cx('valueStatistic', 'due')}>
            {formatCurrencyUSD({ value: invoiceClientDetail?.dueThisWeek, showDecimal: true })}
          </Typography>
        </Grid2>

        <Grid2 className={cx('statistic')} size={3}>
          <Typography className={cx('titleStatistic')}>{t('invoice_title_paid_last_30_days')}</Typography>

          <Typography className={cx('valueStatistic', 'paid')}>
            {formatCurrencyUSD({ value: invoiceClientDetail?.paidAmount30Days, showDecimal: true })}
          </Typography>
        </Grid2>
      </Grid2>

      <Box component={'div'} className={cx('body')}>
        <BaseTable
          dataSource={invoiceClientDetail?.listInvoice || []}
          currentPage={pageSelected}
          totalPage={pagination?.totalPages}
          columns={columns(t, sortBy, handleSort, handleView, handleEdit, handleSendInvoice, handleDelete)}
          onPageChange={handlePageChange}
          onRowClick={(id, record) => handleView(record)}
        />
      </Box>

      {isShowConfirmDelete && (
        <ConfirmModal
          type='delete'
          icon={icons.tableIconActionDelete}
          title={t('common_title_confirm_delete', { ns: 'common/base' })}
          textConfirm={t('common_action_delete', { ns: 'common/base' })}
          onClose={handleCloseConfirmDelete}
          onConfirm={handleConfirmDelete}
        />
      )}

      {isShowUnderDevelopment && <UnderDevelopment onClose={handleShowUnderDevelopment} />}

      {isLoading && <Loading />}
    </Stack>
  );
};

export default InvoiceClientDetailTab;
